import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { APP_LOGO, APP_TITLE, getLoginUrl } from "@/const";
import { Home, TrendingUp, History, BarChart3, Star, Settings, LogOut, Menu, X, HelpCircle, User, Brain } from "lucide-react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import NotificationCenter from "@/components/NotificationCenter";
import MenuArrowGuide from "@/components/MenuArrowGuide";
import { safeSetItem } from "@/lib/safeStorage";
import { soundManager } from "@/lib/soundManager";

export default function Navigation() {
  const { user, isAuthenticated, logout } = useAuth();
  const { openTutorial } = useOnboarding();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/", label: "Home", icon: Home },
    { path: "/predict", label: "Predict", icon: TrendingUp },
    { path: "/statistics", label: "Statistics", icon: BarChart3 },
    { path: "/insights", label: "AI Insights", icon: Brain, authRequired: true },
    { path: "/history", label: "History", icon: History, authRequired: true },
    { path: "/saved", label: "Saved", icon: Star, authRequired: true },
  ];

  const isActive = (path: string) => location === path;

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-card/50 backdrop-blur-lg border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/">
              <div className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
                {APP_LOGO && <img src={APP_LOGO} alt={APP_TITLE} className="h-8 w-8" />}
                <span className="text-xl font-bold bg-gradient-to-r from-primary to-amber-300 bg-clip-text text-transparent">
                  {APP_TITLE}
                </span>
              </div>
            </Link>

            {/* Nav Links */}
            <div className="flex items-center gap-1">
              {navItems.map((item) => {
                if (item.authRequired && !isAuthenticated) return null;
                const Icon = item.icon;
                return (
                  <Link key={item.path} href={item.path}>
                    <div
                      onClick={() => soundManager.playClick()}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all cursor-pointer ${
                        isActive(item.path)
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Auth Section */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { soundManager.playClick(); openTutorial(); }} title="Help & Tutorial">
                <HelpCircle className="w-4 h-4" />
              </Button>
              {isAuthenticated && <NotificationCenter />}
              {isAuthenticated ? (
                <>
                  {/* Profile Picture */}
                  <Link href="/profile">
                    <div className="cursor-pointer hover:opacity-80 transition-opacity" title="Profile Settings">
                      {user?.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={user.name || "Profile"}
                          className="w-8 h-8 rounded-full border-2 border-primary/40 object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center border-2 border-primary/40">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </Link>
                  {user?.role === "admin" && (
                    <Link href="/admin">
                      <Button variant="outline" size="sm">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Button variant="outline" size="sm" onClick={() => { soundManager.playClick(); logout(); }}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <Button asChild size="sm">
                  <a href={getLoginUrl()}>Login</a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="md:hidden bg-card/50 backdrop-blur-lg border-b border-border sticky top-0 z-50">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Logo */}
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              {APP_LOGO && <img src={APP_LOGO} alt={APP_TITLE} className="h-8 w-8" />}
              <span className="text-lg font-bold bg-gradient-to-r from-primary to-amber-300 bg-clip-text text-transparent">
                {APP_TITLE}
              </span>
            </div>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => {
              soundManager.playClick();
              setMobileMenuOpen(!mobileMenuOpen);
              // Mark menu guide as seen when user clicks menu
              safeSetItem("menu_guide_shown", "true");
            }}
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-border bg-card/95 backdrop-blur-lg">
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => {
                if (item.authRequired && !isAuthenticated) return null;
                const Icon = item.icon;
                return (
                  <Link key={item.path} href={item.path}>
                    <div
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                        isActive(item.path)
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                );
              })}

              <div className="pt-4 border-t border-border space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    soundManager.playClick();
                    openTutorial();
                    setMobileMenuOpen(false);
                  }}
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help & Tutorial
                </Button>
                {isAuthenticated ? (
                  <>
                    <Link href="/profile">
                      <Button variant="outline" className="w-full justify-start" onClick={() => setMobileMenuOpen(false)}>
                        <User className="w-4 h-4 mr-2" />
                        Profile Settings
                      </Button>
                    </Link>
                    {user?.role === "admin" && (
                      <Link href="/admin">
                        <Button variant="outline" className="w-full justify-start" onClick={() => setMobileMenuOpen(false)}>
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Panel
                        </Button>
                      </Link>
                    )}
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        soundManager.playClick();
                        logout();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <Button asChild className="w-full">
                    <a href={getLoginUrl()}>Login</a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Menu Arrow Guide - shows for first-time users */}
      <MenuArrowGuide />

      {/* Bottom Mobile Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-lg border-t border-border z-50">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.slice(0, 5).map((item) => {
            if (item.authRequired && !isAuthenticated) return null;
            const Icon = item.icon;
            return (
              <Link key={item.path} href={item.path}>
                <div
                  onClick={() => soundManager.playClick()}
                  className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all cursor-pointer ${
                    isActive(item.path)
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs">{item.label}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
